import { useEffect, useState } from "react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import ChatWidget from "@/components/ChatWidget";
import Seo from "@/components/Seo";
import Editable from "@/components/Editable";
import api from "@/lib/api";
import { Package, Check, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export default function Packages() {
  const [packages, setPackages] = useState([]);
  const [itemMap, setItemMap] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/packages").then((r) => r.data),
      api.get("/products").then((r) => r.data),
      api.get("/courses").then((r) => r.data),
    ]).then(([pkgs, products, courses]) => {
      setPackages(pkgs);
      const map = {};
      products.forEach((p) => { map[p.id] = { label: p.name, kind: p.type }; });
      courses.forEach((c) => { map[c.id] = { label: c.title, kind: "course" }; });
      setItemMap(map);
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="public-packages">
      <Seo title="Packages | Puppl" description="Bundle puppy school, online courses and gear into one money-saving package." canonical={typeof window !== "undefined" ? window.location.origin + "/packages" : ""} />
      <PublicHeader />

      <section className="bg-[#1b4a76] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <Editable id="packages.eyebrow" as="p" className="text-sm uppercase tracking-wider font-semibold text-[#E2B054] mb-2" defaultHtml="Packages" />
          <Editable id="packages.heading" as="h1" className="font-heading text-4xl lg:text-5xl font-bold leading-tight" defaultHtml="Bundle &amp; save." />
          <Editable id="packages.subtitle" as="p" className="text-lg opacity-90 mt-3 max-w-2xl" defaultHtml="Combine in-person classes, online courses and products into one money-saving package." />
        </div>
      </section>

      <section className="py-14 px-6 lg:px-12 max-w-6xl mx-auto">
        {packages.length === 0 ? (
          <div className="card-pp p-16 text-center text-stone-500">
            <Package size={48} weight="duotone" className="mx-auto mb-3 text-stone-300" />
            No packages live yet — our working on it!
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {packages.map((p) => (
              <div key={p.id} className="card-pp p-6 flex flex-col" data-testid={`package-${p.slug}`}>
                {p.badge && <span className="self-start text-[10px] uppercase tracking-wider font-bold bg-[#f38838] text-white px-2.5 py-1 rounded mb-3">{p.badge}</span>}
                {p.image_url && <img loading="lazy" src={p.image_url} alt={p.name} className="rounded-lg w-full h-40 object-cover mb-4" />}
                <h2 className="font-heading text-2xl font-bold text-stone-900">{p.name}</h2>
                <div className="font-heading font-bold text-3xl text-[#f38838] mt-2 mb-3">${p.price}</div>
                <p className="text-stone-600 text-sm mb-4 flex-1">{p.description}</p>
                {p.item_ids?.length > 0 && (
                  <ul className="text-sm text-stone-700 space-y-1.5 mb-5">
                    {p.item_ids.map((id) => itemMap[id] && (
                      <li key={id} className="flex items-start gap-2"><Check size={14} weight="bold" className="text-emerald-600 mt-1 flex-shrink-0" /> <span>{itemMap[id].label}</span></li>
                    ))}
                  </ul>
                )}
                <Link to="/contact" className="btn-primary inline-flex items-center justify-center gap-2 text-sm" data-testid={`package-cta-${p.slug}`}>
                  Enquire to book <ArrowRight size={14} weight="bold" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>

      <PublicFooter />
      <ChatWidget />
    </div>
  );
}
